import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { forkJoin, from, Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SignatureRequestData } from '../models/SignatureRequestData';
import { SignatureResponseData } from '../models/SignatureResponseData';
import { SignatureSessionService } from './signature-session.service';
import { CertificateValidationResponse } from '../models/certificateValidationResponse';
import { SignatureType } from '../models/SignatureType';
import { CertificateRequestData } from '../models/CertificateRequestData';
import { LogService } from '../../shared/services/log.service';

// global variable
declare var SCWSIMPL: any;
declare var SCWS: any;

@Injectable()
export class ScwsService {
  public scws: any;
  public scwsImpl: any;
  private pisteBaseUrl = environment.pisteBaseUrl;

  constructor(private http: HttpClient, private signatureSessionService: SignatureSessionService, private LOG: LogService) {
    this.scws = SCWS;
    this.scwsImpl = SCWSIMPL;
  }

  /**
   * Get the data to sign from server, this data is created by DSS
   */
  public getDataToSign(certificate: any, documentToSign: File, signatureType: SignatureType): Observable<SignatureResponseData> {
    const signatureRequestData = new SignatureRequestData();
    signatureRequestData.certificate = certificate;
    signatureRequestData.signatureTypeEnum = signatureType;
    const formData = new FormData();
    formData.append('signatureRequest', new Blob([JSON.stringify(signatureRequestData)], { type: 'application/json' }));
    formData.append('documentToSign', documentToSign);
    return this.http.post<any>(this.pisteBaseUrl + '/signature/dataToSign', formData, { withCredentials: true });
  }

  /**
   *
   */
  public verifyCertificate(certificate: any): Observable<CertificateValidationResponse> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const certificateRequestData = new CertificateRequestData();
    certificateRequestData.certificateB64 = certificate;
    return this.http.post<any>(this.pisteBaseUrl + '/certificate/verify', certificateRequestData, { headers, withCredentials: true });
  }

  /**
   * Create the final signed document in the server using DSS
   */
  public sign(certificate: string, imprint: string, documentToSign: File): Observable<SignatureResponseData> {
    const signatureRequestData = new SignatureRequestData();
    signatureRequestData.certificate = certificate;
    signatureRequestData.imprint = imprint;
    signatureRequestData.uuid = this.signatureSessionService.signatureResponseData.uuid;
    signatureRequestData.signatureTypeEnum = this.signatureSessionService.signatureRequestData.signatureTypeEnum;
    const formData = new FormData();
    formData.append('signatureRequest', new Blob([JSON.stringify(signatureRequestData)], { type: 'application/json' }));
    formData.append('documentToSign', documentToSign);
    return this.http.post<any>(this.pisteBaseUrl + '/signature/signDocument', formData, { withCredentials: true });
  }

  /**
   * Disconnecting the used reader
   */
  public disconnect() {
    this.scwsImpl.pin.logout();
  }

  /**
   * connect to the certificate with this pin
   */
  public getPrivateKeys(cert: any, pin: any): Observable<any> {
    const cardpin = this.getCardPin(cert);
    return from(cardpin.login(pin)).pipe(flatMap(() => {
      return this.getObjectsFromReaders()
        .pipe(flatMap((arrayOArray) => {
          return arrayOArray.flat().filter((obj: { type: string; ckId: any; }) => obj.type === 'privateKey' && cert.ckId === obj.ckId);
        }));
    }));
  }

  public getCardPin(cert: any): any {
    return cert.parent.pins[cert.pinNumber];
  }

  public getSignaturePin(cert: any): any {
    return cert.parent.pins.filter(element => element.label === 'Signature PIN');
  }

  /**
   * get All objects from reader by name
   */
  public getObjectsFromReaders(): Observable<any> {
    return this.getReaders().pipe(flatMap((readers) => {
      const allreaders: Observable<any>[] = [];
      for (const reader of readers) {
        if (reader.cardPresent) {
          this.scwsImpl.readerName = reader.name;
          const data = this.getTokenFromReader(reader).pipe(flatMap((datas) => datas));
          allreaders.push(data);
        }
      }
      return forkJoin(allreaders);
    }));
  }

  /**
   * get All reader
   */
  private getReaders(): Observable<any> {
    return this.getSCWS().pipe(map(() => {
      return this.scws.readers;
    }));
  }

  /**
   * Get all objects from reader
   */
  private getTokenFromReader(reader: any): Observable<any> {

    return from(reader.connect().then(function getToken(token) {
      console.log('this is data', token.getObjects())
      return from(token.getObjects());
    }).catch(() => {
      return of(new Array());
    }));
  }

  /**
   * Convert base64 to blob
   */
  public b64toBlob(b64Data: string, contentType: any): Blob {
    const sliceSize = 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  public connectSCWS(): Observable<any> {
    SCWSIMPL.scwsHashCode = environment.scwsHashCode;
    console.log('FROM connectSCWS');
    return this.getSCWS().pipe(map(() => from(SCWS.findService(SCWSIMPL.scwsHashCode))));
  }


  private getSCWS(): Observable<any> {
    SCWSIMPL.scwsHashCode = environment.scwsHashCode;
    SCWSIMPL.pisteBaseUrl = environment.pisteBaseUrl;
    return from(new Promise(function createPromise(resolve) {
      console.log('FROM getSCWS');
      resolve(SCWS.findService(SCWSIMPL.scwsHashCode).then(function challengeServer(challenge: string) {
        console.log('Connection to SCWS succeeded');
        return new Promise(function connect(resolve, reject) {
          const req = new XMLHttpRequest();
          req.onreadystatechange = function onreadystatechange() {
            if (this.readyState === 4) {
              if (this.status === 202) {
                resolve(req.responseText);
              } else {
                reject(new Error('Challenge signature failed'));
              }
            }
          };
          req.open('GET', SCWSIMPL.pisteBaseUrl + '/signature/sign_env_challenge?rnd=' + challenge, true);
          req.send();
        })
      }).catch(function onError(err: { message: string; }) {
        console.log('ERROR: ' + err.message);
      }));
    }));
  }

  public getIdopteVersion() {
    return from(SCWS.getProductInfo());
  }

  public getSoftToken(): Observable<any> {
    this.LOG.log('FROM getSoftToken');
    return from(SCWS.getSoftToken());
  }

  public saveEnvironment() {
    console.log('Saving environment...');
    return SCWS.saveEnvironment();
  }

  public restoreEnvironment(env: any): Observable<any> {
    console.log('Environment restoration...');
    return from(SCWS.restoreEvironment(JSON.parse(env)));
  }

  public createEnvironment() {
    return new Promise((resolve) =>
      this.getSCWS().subscribe((signature) => {
        const signFromService = JSON.parse(signature)['cryptogram'];
        console.log("Creation d'un environnement...");
        resolve(SCWS.createEnvironment(signFromService))
      }))
  }
  
  public checkReadersStates(data: any) {
    if (data.length === 0) {
      this.LOG.log('No readers found');
    }

    SCWS.onreaderadded = function onreaderadded(reader: any) {
      console.log('Reader inserted: ', reader);
    };
    SCWS.onreaderremoved = function onreaderremoved(reader: any) {
      console.log('Reader removed: ', reader);
    };
    SCWS.onreaderstatechanged = function onreaderstatechanged(reader: any) {
      console.log('Reader state changed: ', reader);
    };
  }
}
